/**
 * Shared location filter engine for LocationManagement and dynamic location views.
 */

export interface LocationForFilter {
  locationId: string;
  locationName: string;
  address?: string;
  contactPerson?: string;
  contactPhone?: string;
  latitude?: number;
  longitude?: number;
}

export interface LocationFilters {
  hasCoordinates: string; // 'all' | 'true' | 'false'
  hasContact: string;     // 'all' | 'true' | 'false'
  hasAddress: string;     // 'all' | 'true' | 'false'
  searchQuery?: string;
}

export interface BusinessLocationView {
  id: string;
  label: string;
  sortOrder: number;
  showInWeb: boolean;
  showInMobile: boolean;
  icon?: string;
  filters: Partial<LocationFilters>;
}

export const DEFAULT_LOCATION_FILTERS: LocationFilters = {
  hasCoordinates: 'all',
  hasContact: 'all',
  hasAddress: 'all',
};

export function applyLocationFilters<T extends LocationForFilter>(
  locations: T[],
  filters: LocationFilters,
  searchQuery: string
): T[] {
  let filtered = [...locations];

  const q = (searchQuery || filters.searchQuery || '').trim();
  if (q) {
    const lower = q.toLowerCase();
    filtered = filtered.filter(l =>
      l.locationName?.toLowerCase().includes(lower) ||
      l.address?.toLowerCase().includes(lower) ||
      l.contactPerson?.toLowerCase().includes(lower) ||
      l.contactPhone?.toLowerCase().includes(lower)
    );
  }

  if (filters.hasCoordinates === 'true') {
    filtered = filtered.filter(l =>
      typeof l.latitude === 'number' && typeof l.longitude === 'number' &&
      isFinite(l.latitude) && isFinite(l.longitude)
    );
  } else if (filters.hasCoordinates === 'false') {
    filtered = filtered.filter(l =>
      l.latitude == null || l.longitude == null ||
      !isFinite(Number(l.latitude)) || !isFinite(Number(l.longitude))
    );
  }

  if (filters.hasContact === 'true') {
    filtered = filtered.filter(l => Boolean(l.contactPerson?.trim() || l.contactPhone?.trim()));
  } else if (filters.hasContact === 'false') {
    filtered = filtered.filter(l => !l.contactPerson?.trim() && !l.contactPhone?.trim());
  }

  if (filters.hasAddress === 'true') {
    filtered = filtered.filter(l => Boolean(l.address?.trim()));
  } else if (filters.hasAddress === 'false') {
    filtered = filtered.filter(l => !l.address?.trim());
  }

  return filtered;
}

export function filtersFromLocationView(view: BusinessLocationView): { filters: LocationFilters; searchQuery: string } {
  const f = view.filters ?? {};
  return {
    filters: {
      hasCoordinates: f.hasCoordinates ?? 'all',
      hasContact: f.hasContact ?? 'all',
      hasAddress: f.hasAddress ?? 'all',
    },
    searchQuery: f.searchQuery ?? '',
  };
}

export function isLocationFiltersDefault(filters: LocationFilters, searchQuery: string): boolean {
  return (
    !searchQuery.trim() &&
    filters.hasCoordinates === 'all' &&
    filters.hasContact === 'all' &&
    filters.hasAddress === 'all'
  );
}
